const UPPER_QUADRANTS = [1, 2, 5, 6];
const RIGHT_QUADRANTS = [1, 4, 5, 8];

const range = (from, to) => {
  const step = from <= to ? 1 : -1;
  const list = [];
  for (let i = from; i !== to + step; i += step) list.push(i);
  return list;
};

export const TOOTH_CONDITIONS = [
  { value: 'healthy', label: 'Sadio', description: 'Dente em perfeito estado' },
  { value: 'carious', label: 'Cariado', description: 'Dente com cárie' },
  { value: 'restored', label: 'Restaurado', description: 'Dente com restauração' },
  { value: 'crown', label: 'Coroa', description: 'Dente com coroa protética' },
  { value: 'canal', label: 'Canal', description: 'Dente com tratamento de canal' },
  { value: 'missing', label: 'Ausente', description: 'Dente ausente' },
];

export const TOOTH_SURFACES = ['Vestibular', 'Mesial', 'Oclusal', 'Distal', 'Lingual'];

export const ODONTOGRAM_ROWS = [
  {
    key: 'upper-permanent',
    arch: 'upper',
    type: 'permanent',
    right: range(18, 11),
    left: range(21, 28),
  },
  {
    key: 'upper-deciduous',
    arch: 'upper',
    type: 'deciduous',
    right: range(55, 51),
    left: range(61, 65),
  },
  {
    key: 'lower-deciduous',
    arch: 'lower',
    type: 'deciduous',
    right: range(85, 81),
    left: range(71, 75),
  },
  {
    key: 'lower-permanent',
    arch: 'lower',
    type: 'permanent',
    right: range(48, 41),
    left: range(31, 38),
  },
];

export const TOOTH_NUMBERS = ODONTOGRAM_ROWS.reduce(
  (acc, row) => acc.concat(row.right, row.left),
  [],
);

export const DECIDUOUS_NUMBERS = ODONTOGRAM_ROWS
  .filter(row => row.type === 'deciduous')
  .reduce((acc, row) => acc.concat(row.right, row.left), []);

export function getToothQuadrant(number) {
  return Math.floor(number / 10);
}

export function getToothArch(number) {
  return UPPER_QUADRANTS.includes(getToothQuadrant(number)) ? 'upper' : 'lower';
}

export function getToothType(number) {
  return getToothQuadrant(number) >= 5 ? 'deciduous' : 'permanent';
}

export function isAnteriorTooth(number) {
  return number % 10 <= 3;
}

export function conditionLabel(condition) {
  const found = TOOTH_CONDITIONS.find(item => item.value === condition);
  return found ? found.label : condition || '';
}

export function getSurfaceLabel(number, surface) {
  if (surface === 'Lingual' && getToothArch(number) === 'upper') return 'Palatina';
  if (surface === 'Oclusal' && isAnteriorTooth(number)) return 'Incisal';
  return surface;
}

export function getSurfaceDescription(number, surface) {
  const descriptions = {
    Vestibular: 'lado externo, voltado para a bochecha ou lábio',
    Mesial: 'lado voltado para a linha média',
    Distal: 'lado oposto à linha média',
    Lingual: 'lado interno, voltado para a língua',
    Palatina: 'lado interno, voltado para o palato',
    Oclusal: 'superfície de mastigação',
    Incisal: 'borda de corte',
  };
  const label = getSurfaceLabel(number, surface);
  return `${label} - ${descriptions[label]}`;
}

export function getSurfaceLayout(number) {
  const isUpper = getToothArch(number) === 'upper';
  const isPatientRight = RIGHT_QUADRANTS.includes(getToothQuadrant(number));

  return [
    { zone: 'top', surface: isUpper ? 'Vestibular' : 'Lingual' },
    { zone: isPatientRight ? 'right' : 'left', surface: 'Mesial' },
    { zone: 'center', surface: 'Oclusal' },
    { zone: isPatientRight ? 'left' : 'right', surface: 'Distal' },
    { zone: 'bottom', surface: isUpper ? 'Lingual' : 'Vestibular' },
  ];
}

export function getOrderedSurfaces(number) {
  return getSurfaceLayout(number).map(item => item.surface);
}

export function transformApiDataToOdontograma(apiData) {
  let inputsObject = {};

  if (apiData && apiData.inputs) {
    if (Array.isArray(apiData.inputs)) {
      apiData.inputs.forEach((input) => {
        inputsObject[input.name] = input.value || '';
      });
    } else if (typeof apiData.inputs === 'object') {
      inputsObject = apiData.inputs;
    }
  }

  return TOOTH_NUMBERS.map((number) => {
    const surfacesString = inputsObject[`tooth${number}Surfaces`] || '';
    const surfaces = surfacesString
      ? surfacesString.split(',').map(s => s.trim()).filter(s => s)
      : [];

    return {
      number,
      condition: inputsObject[`tooth${number}Condition`] || '',
      notes: inputsObject[`tooth${number}Notes`] || '',
      surfaces,
    };
  });
}

export function transformOdontogramaToApiData(teethArray, existingInputs = []) {
  const updatedValues = {};

  teethArray.forEach((tooth) => {
    const {
      number, condition, notes, surfaces,
    } = tooth;

    updatedValues[`tooth${number}Surfaces`] = surfaces && surfaces.length > 0
      ? surfaces.join(', ')
      : '';
    updatedValues[`tooth${number}Condition`] = condition || '';
    updatedValues[`tooth${number}Notes`] = notes || '';
  });

  const inputs = existingInputs.map(input => ({
    ...input,
    value: updatedValues[input.name] !== undefined ? updatedValues[input.name] : input.value,
  }));

  return inputs;
}
